import { Flex } from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  CheckSquare,
  ChevronDown,
  ChevronRight,
  Instagram,
  Square,
  Youtube,
} from "lucide-react";
import React from "react";
import styles from "../styles.module.scss";
import {
  CampaignTimelineStageId,
  DragMode,
  DragState,
  GanttRowRecord,
} from "../types";
import { GanttTimelineHeaderCell } from "./GanttTimelineHeaderCell";
import { GanttTrack } from "./GanttTrack";

interface BuildGanttColumnsParams {
  editMode: boolean;
  allCreatorsSelected: boolean;
  someCreatorsSelected: boolean;
  selectedCreatorIds: Set<string | number>;
  expandedRowKeys: readonly React.Key[];
  ticks: Date[];
  todayPct: number;
  drag: DragState | null;
  selectAllCreators: () => void;
  clearCreatorSelection: () => void;
  toggleCreatorSelected: (id: string | number) => void;
  toggleCollapse: (creatorId: string | number) => void;
  registerTrackRef: (key: string, el: HTMLDivElement | null) => void;
  beginDrag: (
    e: React.PointerEvent,
    mode: DragMode,
    row: {
      creatorId: string | number;
      deliverableName: string;
      deliverableId: string;
    },
    stageId: CampaignTimelineStageId
  ) => void;
  onOpenDeliverable?: (
    creatorId: string,
    deliverableId?: string,
    taskId?: string
  ) => void;
}

export function buildGanttColumns({
  editMode,
  allCreatorsSelected,
  someCreatorsSelected,
  selectedCreatorIds,
  expandedRowKeys,
  ticks,
  todayPct,
  drag,
  selectAllCreators,
  clearCreatorSelection,
  toggleCreatorSelected,
  toggleCollapse,
  registerTrackRef,
  beginDrag,
  onOpenDeliverable,
}: BuildGanttColumnsParams): ColumnsType<GanttRowRecord> {
  return [
    {
      title: (
        <Flex align="center" gap={8} className={styles.columnHeaderCreator}>
          {editMode && (
            <button
              type="button"
              onClick={
                allCreatorsSelected ? clearCreatorSelection : selectAllCreators
              }
              className={styles.headerCheckboxBtn}
              aria-label={
                allCreatorsSelected
                  ? "Clear all creators"
                  : "Select all creators"
              }
            >
              {allCreatorsSelected ? (
                <CheckSquare size={15} className={styles.checkSquareIcon} />
              ) : (
                <Square size={15} className={styles.squareIcon} />
              )}
            </button>
          )}
          <span>Creator / deliverable</span>
        </Flex>
      ),
      dataIndex: "influencerName",
      key: "creator_deliverable",
      width: 220,
      onCell: (record: GanttRowRecord) => {
        if (record.rowType === "creator") {
          const isSelected = selectedCreatorIds.has(record.creatorId);
          return {
            colSpan: 2,
            className: `${styles.creatorRowCell} ${
              editMode && isSelected ? styles.selectedCreatorRow : ""
            }`,
          };
        }
        return {
          colSpan: 1,
          className: styles.deliverableRowCell,
        };
      },
      render: (_: unknown, record: GanttRowRecord) => {
        if (record.rowType === "creator") {
          const displayName =
            record.influencerName || record.creatorName || "";
          const isSelected = selectedCreatorIds.has(record.creatorId);
          const isCollapsed = !expandedRowKeys.includes(
            `creator-${record.creatorId}`
          );
          const PlatformIcon =
            record.creatorPlatform === "youtube" ? Youtube : Instagram;

              const hasDeliverables = (record.deliverableCount ?? 0) > 0;

              return (
                <div className={styles.creatorBanner}>
                  {editMode && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleCreatorSelected(record.creatorId);
                      }}
                      className={styles.creatorSelectBtn}
                      aria-label={
                        isSelected
                          ? `Deselect ${displayName}`
                          : `Select ${displayName}`
                      }
                    >
                      {isSelected ? (
                        <CheckSquare
                          size={16}
                          className={styles.checkSquareIcon}
                        />
                      ) : (
                        <Square size={16} className={styles.squareIcon} />
                      )}
                    </button>
                  )}
                  <div
                    role={hasDeliverables ? "button" : undefined}
                    tabIndex={hasDeliverables ? 0 : -1}
                    onClick={() => {
                      if (hasDeliverables) {
                        toggleCollapse(record.creatorId);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (hasDeliverables && (e.key === "Enter" || e.key === " ")) {
                        toggleCollapse(record.creatorId);
                      }
                    }}
                    className={styles.creatorRowContent}
                    style={{ cursor: hasDeliverables ? "pointer" : "default" }}
                  >
                    {isCollapsed ? (
                        <ChevronRight size={14} className={styles.chevronIcon} style={{visibility: !hasDeliverables ? "hidden" : "visible"}}/>
                      ) : (
                        <ChevronDown size={14} className={styles.chevronIcon} style={{visibility: !hasDeliverables ? "hidden" : "visible"}}/>
                      )}
                {record.creatorImage ? (
                  <img
                    src={record.creatorImage}
                    alt=""
                    className={styles.creatorAvatar}
                  />
                ) : (
                  <div className={styles.creatorAvatarFallback}>
                    {(displayName || "?").charAt(0).toUpperCase()}
                  </div>
                )}
                <span className={styles.creatorName}>{displayName}</span>
                <PlatformIcon
                  size={12}
                  className={
                    record.creatorPlatform === "youtube"
                      ? styles.youtubeIcon
                      : styles.instagramIcon
                  }
                />
                <span className={styles.creatorHandle}>{record.creatorHandle}</span>
                <span className={styles.deliverableCountBadge}>
                  {record.deliverableCount} deliv.
                </span>
              </div>
            </div>
          );
        }

        return (
          <button
            type="button"
            className={styles.deliverableCellBtn}
            onClick={() => {
              if (record.deliverableId && onOpenDeliverable) {
                onOpenDeliverable(String(record.creatorId), record.deliverableId);
              }
            }}
          >
            <p className={styles.deliverableName}>{record.deliverableName}</p>
            <p className={styles.deliverableStatus}>
              {(record.deliverableStatus || "").replace(/_/g, " ")}
            </p>
          </button>
        );
      },
    },
    {
      title: <GanttTimelineHeaderCell ticks={ticks} todayPct={todayPct} />,
      dataIndex: "timeline",
      key: "timeline",
      onCell: (record: GanttRowRecord) => {
        if (record.rowType === "creator") {
          return { colSpan: 0 };
        }
        return {
          colSpan: 1,
          className: styles.timelineTrackCell,
        };
      },
      render: (_: unknown, record: GanttRowRecord) => {
        if (record.rowType === "creator") {
          return null;
        }
        const trackKey = `${record.creatorId}-${record.deliverableId}`;
        const rowInSelection =
          selectedCreatorIds.has(record.creatorId) ||
          selectedCreatorIds.size === 0;

        return (
          <GanttTrack
            trackKey={trackKey}
            creatorId={record.creatorId}
            deliverableId={record.deliverableId || ""}
            deliverableName={record.deliverableName || ""}
            stages={record.stages || []}
            ticks={ticks}
            todayPct={todayPct}
            editMode={editMode}
            rowInSelection={rowInSelection}
            drag={drag}
            registerTrackRef={registerTrackRef}
            beginDrag={beginDrag}
            onOpenDeliverable={onOpenDeliverable}
          />
        );
      },
    },
  ];
}
